/* eslint-disable react/prop-types */
// ──────────────────────────────────────────────────────────────
// src/scenes/finance-reimbursement/index.jsx
// – loads ONLY the selected School Year (current SY by default)
// – Month + Program filters still work
// – Alternating row colours & line-chart fixed
// ──────────────────────────────────────────────────────────────
import React, { useState, useEffect } from "react";
import {
    Box, Typography, FormControl, InputLabel,
    Select, MenuItem, Button, useTheme
} from "@mui/material";
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell
} from "recharts";
import { DataGrid } from "@mui/x-data-grid";
import { styled, alpha, darken, lighten } from "@mui/material/styles";
import axios from "axios";

import { tokens } from "../../theme";
import Header from "../../component-files/Header";
import StatBox from "../../component-files/StatBox";
import MediaViewer from "../../components/mediaviewer";

/* ──────────────────────────────────────────────────────────── *
 *  ENDPOINTS & CONSTANTS                                      *
 * ──────────────────────────────────────────────────────────── */
const API_EXPENSES =
    "https://figazuresql.swancreate.com/api/swa/FINANCE_expenses";
const API_PROGRAMS =
    "https://figazuresql.swancreate.com/api/swa/FINANCE_ReimbursementPrograms";

const COLORS = [
    "#D62828", "#FF8C00", "#F3BA2F", "#2E8B57",
    "#1E90FF", "#6A0DAD", "#00CED1", "#FF1493",
    "#A52A2A", "#20B2AA"
];

const money = (v) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v);

/* ─── School-Year helpers (Jul 1 → Jun 30) ─────────────────── */
const getCurrentSYStart = (d = new Date()) =>
    d.getMonth() >= 6 ? d.getFullYear() : d.getFullYear() - 1;
const syLabel = (y) => `${y}-${y + 1}`;
const inSY = (date, start) =>
    date >= new Date(start, 6, 1) && date < new Date(start + 1, 6, 1);

/* ──────────────────────────────────────────────────────────── */
/*  STRIPED DATAGRID                                            */
/* ──────────────────────────────────────────────────────────── */
const StripedGrid = styled(DataGrid)(({ theme }) => ({
    border: "none",
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    "& .MuiDataGrid-columnHeaders": {
        backgroundColor:
            theme.palette.mode === "dark"
                ? darken(theme.palette.background.paper, .1)
                : lighten(theme.palette.background.paper, .06),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    "& .MuiDataGrid-row--even": {
        backgroundColor:
            theme.palette.mode === "dark"
                ? alpha(theme.palette.primary.light, .15)
                : alpha(theme.palette.primary.main, .04),
    },
    "& .MuiDataGrid-row--odd": {
        backgroundColor:
            theme.palette.mode === "dark"
                ? alpha(theme.palette.primary.main, .10)
                : alpha(theme.palette.primary.light, .08),
    },
    "& .MuiDataGrid-cell": { borderBottom: `1px solid ${theme.palette.divider}` },
}));

/* ──────────────────────────────────────────────────────────── */
/*  COMPONENT                                                   */
/* ──────────────────────────────────────────────────────────── */
export default function DashboardReimbursement() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const accent = colors.greenAccent[500];

    /* ── state ─────────────────────────────────────────────── */
    const [expenses, setExpenses] = useState([]);
    const [programs, setPrograms] = useState([]);

    const [syStart, setSyStart] = useState(getCurrentSYStart());
    const [monthFilt, setMonthFilt] = useState("All");
    const [progFilt, setProgFilt] = useState("All");

    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerURL, setViewerURL] = useState("");

    /* ── fetch once ────────────────────────────────────────── */
    useEffect(() => {
        (async () => {
            try {
                const [exp, prog] = await Promise.all([
                    axios.get(API_EXPENSES),
                    axios.get(API_PROGRAMS),
                ]);
                const norm = exp.data.map((r) => ({
                    ...r,
                    ExpenseDate: r.ExpenseDate ? new Date(r.ExpenseDate) : null,
                    Amount: +r.Amount || 0,
                    POAmount: +r.POAmount || 0,
                }));
                setExpenses(norm);
                setPrograms(prog.data);
            } catch (err) {
                console.error("Fetch error:", err);
            }
        })();
    }, []);

    /* ── filtered rows ─────────────────────────────────────── */
    const rows = expenses.filter((e) => {
        if (e.ReimbursementTrackingId == null) return false;
        if (!e.ExpenseDate || !inSY(e.ExpenseDate, syStart)) return false;

        const mOK =
            monthFilt === "All" ||
            e.ExpenseDate.getMonth() + 1 === +monthFilt;
        const pOK =
            progFilt === "All" ||
            e.ReimbursmentTrackingProgram === progFilt;

        return mOK && pOK;
    });

    /* ── totals by program ─────────────────────────────────── */
    const totals = rows.reduce((a, r) => {
        const p = r.ReimbursmentTrackingProgram;
        a[p] = (a[p] || 0) + r.Amount;
        return a;
    }, {});
    const progs = Object.keys(totals);

    /* ── Pie data ──────────────────────────────────────────── */
    const pieData = progs.map((p) => ({ name: p, value: totals[p] }));

    /* ── Line rows (Jul→Jun) ───────────────────────────────── */
    const labels = Array.from({ length: 12 }, (_, i) =>
        new Date(syStart, 6 + i, 1).toLocaleString("default", { month: "short" })
    );
    const lineRows = labels.map((lab) => ({ month: lab }));
    progs.forEach((p) => {
        lineRows.forEach((r) => (r[p] = 0));
    });

    rows.forEach((r) => {
        const d = r.ExpenseDate;
        const idx =
            (d.getFullYear() === syStart ? d.getMonth() - 6 : d.getMonth() + 6);
        if (idx >= 0 && idx < 12) {
            lineRows[idx][r.ReimbursmentTrackingProgram] += r.Amount;
        }
    });

    /* ── month list for dropdown (inside current SY) ──────── */
    const months = Array.from(
        new Set(
            expenses
                .filter((e) => e.ExpenseDate && inSY(e.ExpenseDate, syStart))
                .map((e) =>
                    new Date(0, e.ExpenseDate.getMonth()).toLocaleString("default", {
                        month: "long",
                    })
                )
        )
    );

    /* ── helpers ──────────────────────────────────────────── */
    const progLabel = (full) =>
        programs.find((x) => x.program_name === full)?.program_abbreviation ||
        full;

    /* ── grid columns ─────────────────────────────────────── */
    const cols = [
        {
            field: "view",
            headerName: "Invoice",
            width: 110,
            renderCell: ({ row }) => (
                <Button
                    variant="contained"
                    size="small"
                    sx={{ bgcolor: accent }}
                    onClick={() => {
                        setViewerURL(row.imageURL);
                        setViewerOpen(true);
                    }}
                >
                    View
                </Button>
            ),
        },
        { field: "ExpenseID", headerName: "Expense ID", width: 150 },
        {
            field: "ExpenseDate",
            headerName: "Date",
            width: 120,
            valueGetter: ({ row }) =>
                row.ExpenseDate ? row.ExpenseDate.toLocaleDateString() : "",
        },
        {
            field: "Amount",
            headerName: "Amount",
            width: 110,
            valueGetter: ({ row }) => money(row.Amount),
        },
        { field: "SiteName", headerName: "Site", width: 150 },
        {
            field: "ReimbursmentTrackingProgram",
            headerName: "Program",
            width: 180,
        },
    ];

    /* ─────────────────────────────────────────────────────── */
    const themeBg = theme.palette.background.default;
    const chartBg = theme.palette.background.paper;
    const textColor = theme.palette.text.primary;

    return (
        <Box m={2} sx={{ bgcolor: themeBg, color: textColor, p: 2, borderRadius: 2 }}>
            <Header title="FEDERAL PROGRAMS" subtitle="School-Year reimbursement tracking" />

            {/* ── FILTER BAR ────────────────────────────────────── */}
            <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
                {/* School Year */}
                <FormControl sx={{ minWidth: 170 }}>
                    <InputLabel>School Year</InputLabel>
                    <Select
                        label="School Year"
                        value={syStart}
                        onChange={(e) => setSyStart(+e.target.value)}
                    >
                        {Array.from({ length: 5 }, (_, i) => {
                            const y = getCurrentSYStart() - i;
                            return (
                                <MenuItem key={y} value={y}>
                                    {syLabel(y)}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>

                {/* Month */}
                <FormControl sx={{ minWidth: 150 }}>
                    <InputLabel>Month</InputLabel>
                    <Select
                        label="Month"
                        value={monthFilt}
                        onChange={(e) => setMonthFilt(e.target.value)}
                    >
                        <MenuItem value="All">All</MenuItem>
                        {months.map((m, i) => (
                            <MenuItem key={i + 1} value={i + 1}>
                                {m}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Program */}
                <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Program</InputLabel>
                    <Select
                        label="Program"
                        value={progFilt}
                        onChange={(e) => setProgFilt(e.target.value)}
                    >
                        <MenuItem value="All">All</MenuItem>
                        {progs.map((p) => (
                            <MenuItem key={p} value={p}>
                                {progLabel(p)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* ── STAT BOXES ───────────────────────────────────── */}
            <Box
                display="grid"
                gridTemplateColumns="repeat(auto-fit, minmax(220px,1fr))"
                gap={2}
                mb={4}
            >
                {progs.map((p, i) => (
                    <StatBox
                        key={p}
                        title={money(totals[p])}
                        subtitle={`Reimb • ${progLabel(p)}`}
                        borderColor={COLORS[i % COLORS.length]}
                    />
                ))}
            </Box>

            {/* ── CHARTS ───────────────────────────────────────── */}
            <Box display="flex" flexWrap="wrap" gap={2} mb={4}>
                {/* Pie */}
                <Box flex={1} minWidth={340} sx={{ p: 2, bgcolor: chartBg, borderRadius: 2 }}>
                    <Typography variant="h6" mb={2}>
                        Distribution ({syLabel(syStart)})
                    </Typography>
                    <ResponsiveContainer width="100%" height={350}>
                        <PieChart>
                            <Pie
                                data={pieData}
                                dataKey="value"
                                nameKey="name"
                                outerRadius={120}
                                label={({ name, value }) =>
                                    `${progLabel(name)}: ${money(value)}`
                                }
                            >
                                {pieData.map((_, i) => (
                                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip formatter={money} />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </Box>

                {/* Line */}
                <Box flex={1} minWidth={340} sx={{ p: 2, bgcolor: chartBg, borderRadius: 2 }}>
                    <Typography variant="h6" mb={2}>
                        Monthly Expenses ({syLabel(syStart)})
                    </Typography>
                    <ResponsiveContainer width="100%" height={350}>
                        <LineChart data={lineRows}>
                            <CartesianGrid strokeDasharray="3 3" stroke={accent} />
                            <XAxis dataKey="month" stroke={textColor} />
                            <YAxis stroke={textColor} />
                            <Tooltip formatter={money} />
                            <Legend />
                            {progs.map((p, i) => (
                                <Line
                                    key={p}
                                    dataKey={p}
                                    type="monotone"
                                    stroke={COLORS[i % COLORS.length]}
                                    strokeWidth={3}
                                    connectNulls
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
            </Box>

            {/* ── GRID ─────────────────────────────────────────── */}
            <Box sx={{ height: 520 }}>
                <StripedGrid
                    rows={rows}
                    columns={cols}
                    getRowId={(r) => r.apiid || r.ExpenseID}
                    pageSize={10}
                    rowsPerPageOptions={[10]}
                    density="compact"
                />
            </Box>

            {/* ── MEDIA VIEWER ─────────────────────────────────── */}
            {viewerOpen && (
                <MediaViewer
                    open={viewerOpen}
                    src={viewerURL}
                    onClose={() => setViewerOpen(false)}
                    type="image"
                />
            )}
        </Box>
    );
}
